'use client'

import { motion, useReducedMotion } from 'framer-motion'

/**
 * Composition décorative du hero.
 *
 * VOLONTAIREMENT ABSTRAITE : aucune capture de produit, aucun chiffre, aucun
 * libellé. Inventer une interface ou des données reviendrait à inventer des
 * fonctionnalités (CLAUDE.md § 6). Les vraies captures produits, une fois
 * fournies par AXIA, remplaceront ce visuel depuis la médiathèque.
 *
 * Animations : deux seulement, lentes et discrètes — le flottement de la
 * composition et la montée des barres. Neutralisées si l'utilisateur réduit
 * les animations.
 */

/** Hauteurs relatives des barres, purement graphiques. */
const BARS = [38, 64, 46, 82, 58, 94, 72]

export function HeroVisual() {
  const prefersReducedMotion = useReducedMotion()

  const float = prefersReducedMotion
    ? {}
    : {
        animate: { y: [0, -10, 0] },
        transition: { duration: 7, repeat: Infinity, ease: 'easeInOut' as const },
      }

  const floatDelayed = prefersReducedMotion
    ? {}
    : {
        animate: { y: [0, -14, 0] },
        transition: { duration: 8, repeat: Infinity, ease: 'easeInOut' as const, delay: 0.8 },
      }

  return (
    <div className="relative aspect-4/3 w-full select-none" aria-hidden="true">
      {/* Halo de marque */}
      <div className="from-primary-300/45 to-accent-300/40 absolute inset-6 rounded-[2.5rem] bg-gradient-to-br blur-3xl" />

      <motion.div
        {...float}
        className="bg-surface/85 ring-border/70 absolute inset-0 rounded-3xl shadow-[var(--shadow-lg)] ring-1 backdrop-blur-sm"
      >
        <div className="border-border/70 flex items-center gap-2 border-b px-6 py-4">
          <span className="bg-primary-500 size-2.5 rounded-full" />
          <span className="bg-accent-400 size-2.5 rounded-full" />
          <span className="bg-ink-200 size-2.5 rounded-full" />
          <span className="bg-ink-100 ms-4 h-2.5 w-28 rounded-full" />
        </div>

        <div className="grid gap-5 p-6 sm:grid-cols-5">
          {/* Colonne latérale */}
          <div className="hidden flex-col gap-3 sm:flex">
            {[0, 1, 2, 3, 4].map((row) => (
              <div
                key={row}
                className={
                  row === 1
                    ? 'from-primary-200 to-primary-100 h-8 rounded-lg bg-gradient-to-r'
                    : 'bg-ink-100 h-8 rounded-lg'
                }
              />
            ))}
          </div>

          {/* Zone principale */}
          <div className="space-y-5 sm:col-span-4">
            <div className="grid grid-cols-3 gap-3">
              {[0, 1, 2].map((tile) => (
                <div key={tile} className="bg-ink-50 ring-border/60 rounded-xl p-4 ring-1">
                  <div className="bg-ink-200 h-2 w-10 rounded-full" />
                  <div className="from-primary-500 to-accent-500 mt-3 h-3 w-16 rounded-full bg-gradient-to-r" />
                </div>
              ))}
            </div>

            {/* Graphique abstrait, sans aucune valeur */}
            <div className="bg-ink-50 ring-border/60 rounded-xl p-5 ring-1">
              <div className="flex h-32 items-end gap-2.5">
                {BARS.map((height, index) => (
                  <motion.div
                    key={height}
                    className={
                      index === 5
                        ? 'from-primary-500 to-accent-500 flex-1 origin-bottom rounded-t-md bg-gradient-to-t'
                        : 'bg-primary-200 flex-1 origin-bottom rounded-t-md'
                    }
                    style={{ height: `${height}%` }}
                    initial={prefersReducedMotion ? false : { scaleY: 0 }}
                    whileInView={{ scaleY: 1 }}
                    viewport={{ once: true }}
                    transition={{
                      duration: 0.7,
                      delay: 0.3 + index * 0.07,
                      ease: [0.16, 1, 0.3, 1],
                    }}
                  />
                ))}
              </div>
            </div>
          </div>
        </div>
      </motion.div>

      {/* Pastille flottante */}
      <motion.div
        {...floatDelayed}
        className="bg-surface ring-border/70 absolute -end-4 -bottom-5 hidden rounded-2xl p-4 shadow-[var(--shadow-md)] ring-1 sm:block"
      >
        <div className="flex items-center gap-3">
          <div className="from-primary-500 to-accent-500 size-9 rounded-xl bg-gradient-to-br" />
          <div className="space-y-2">
            <div className="bg-ink-200 h-2 w-16 rounded-full" />
            <div className="bg-ink-100 h-2 w-10 rounded-full" />
          </div>
        </div>
      </motion.div>
    </div>
  )
}
